/**
 * PATH: packages/email-hub/src/feature/reader-header/command/useCommandBarScrollShadow.ts
 * FILE_ID: EMAILHUB-READERHEADER-SCROLL-SHADOW-001
 * PURPOSE: Tell the sticky command bar when the reader body has scrolled past the shadow threshold.
 * ROLE IN ARCHITECTURE: Presentation hook for the command band (plan "Band 1: sticky command bar").
 * MAIN EXPORTS: useCommandBarScrollShadow.
 * NON-RESPONSIBILITIES: Does not own sticky positioning or scroll containers.
 * NOTES FOR FUTURE AI: The capture-phase document listener works for ANY host scroll
 *   container without prop-drilling a ref through both adapters; keep it passive.
 */
import { useEffect, useState, type RefObject } from 'react'

import { SCROLL_SHADOW_AFTER_PX } from '../tokens'

/**
 * Watch scroll events from the reader body below the header and report
 * whether the bar should paint its separation shadow (>8px scrolled).
 *
 * Inputs: `barRef` — the command bar element (used to scope which scroll
 * containers count: descendants of the header itself, e.g. the More menu,
 * are ignored).
 * Output: `true` once the closest following scroll container passed the
 * plan's 8px threshold; `false` at rest.
 * Failure modes: none — with no scrolling sibling it simply stays `false`.
 */
export function useCommandBarScrollShadow(
  barRef: RefObject<HTMLElement | null>,
): boolean {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = (event: Event) => {
      const bar = barRef.current
      const target = event.target
      if (!bar || !(target instanceof HTMLElement)) return
      // Ignore scrolls inside the header (menus, disclosures) and scrolls in
      // unrelated panes: only a container that CONTAINS the bar (an ancestor
      // scroll area) or follows it in the same reader column may cast the shadow.
      if (bar.contains(target)) return
      const frame = bar.closest('[data-reader-header-surface]') ?? bar
      const related =
        target.contains(frame) ||
        Boolean(
          frame.parentElement && frame.parentElement.contains(target),
        )
      if (!related) return
      setScrolled(target.scrollTop > SCROLL_SHADOW_AFTER_PX)
    }
    document.addEventListener('scroll', onScroll, {
      capture: true,
      passive: true,
    })
    return () =>
      document.removeEventListener('scroll', onScroll, { capture: true })
  }, [barRef])

  return scrolled
}
